import { Head, usePage } from "@inertiajs/react";
import { ArrowLeft, BookOpen, PlayCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { SharedData } from "@/types";

export default function UserManualPage() {
  const { auth } = usePage<SharedData>().props;

  return (
    <>
      <Head title="Guía y Documentación" />
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
        <div className="mx-auto flex max-w-4xl flex-col gap-6 px-4 py-8 sm:gap-8 sm:py-12 lg:py-14">
          <section className="rounded-2xl border border-slate-200 bg-white/90 p-6 shadow-sm backdrop-blur-sm sm:p-8">
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                <BookOpen className="h-4 w-4 text-blue-600" />
                Documentación y Guía
              </span>
            </div>
            <div className="mt-4 space-y-3">
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Todo sobre Synapse: <span className="text-blue-600">De la Solicitud a la Compra</span>
              </h1>
              <p className="text-base leading-relaxed text-slate-600 sm:text-lg">
                Descubrí cómo nuestra IA simplifica tu gestión de compras. Ya sea que estés aprendiendo a usar la plataforma o evaluando sus capacidades, esta guía te llevará paso a paso por todo el ciclo de vida de una compra.
              </p>
            </div>
            {auth.user && (
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild variant="outline" className="gap-2">
                  <a href="/">
                    <ArrowLeft className="h-4 w-4" />
                    Volver al inicio
                  </a>
                </Button>
              </div>
            )}
          </section>

          <article className="prose prose-slate prose-sm sm:prose-base lg:prose-lg max-w-none rounded-2xl border border-slate-200 bg-white/95 px-4 py-8 shadow-sm sm:px-8 lg:px-10 prose-headings:scroll-mt-24 prose-headings:font-bold prose-headings:text-slate-900 prose-p:text-slate-600 prose-li:text-slate-600 prose-img:rounded-xl prose-img:shadow-md prose-a:text-blue-600 hover:prose-a:text-blue-800">
            <ManualContent />
          </article>
        </div>
      </div>
    </>
  );
}

function VideoEmbed({ src, title }: { src: string; title: string }) {
  const isPlaceholder = src.startsWith("{{");

  return (
    <div className="my-8 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm ring-1 ring-slate-900/5 transition-all hover:shadow-md">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-3">
        <PlayCircle className="h-4 w-4 text-blue-600" />
        <span className="text-sm font-medium text-slate-900">{title}</span>
      </div>
      <div className="relative aspect-video w-full bg-slate-900">
        {isPlaceholder ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-slate-100 text-slate-400">
            <PlayCircle className="h-12 w-12 opacity-20" />
            <p className="text-xs font-medium">Video demostrativo próximamente</p>
          </div>
        ) : (
          <video controls className="h-full w-full" preload="metadata">
            <source src={src} type="video/mp4" />
            Tu navegador no soporta el elemento de video.
          </video>
        )}
      </div>
    </div>
  );
}

function ImageFigure({ src, alt, caption }: { src: string; alt: string; caption?: string }) {
  return (
    <figure className="my-8 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm ring-1 ring-slate-900/5 transition-all hover:shadow-md">
      <div className="bg-slate-100/50 p-1 sm:p-2">
        <img src={src} alt={alt} className="h-auto w-full rounded-lg shadow-sm" />
      </div>
      {(caption ?? alt) && (
        <figcaption className="border-t border-slate-200 bg-white px-4 py-3 text-center text-xs font-medium text-slate-500">
          {caption ?? alt}
        </figcaption>
      )}
    </figure>
  );
}

function ManualContent() {
  return (
    <div className="space-y-12 sm:space-y-16">
      <section>
        <h2>¿Qué es Synapse?</h2>
        <p className="lead">
          Synapse es la solución integral para gestionar compras de manera eficiente, diseñada para adaptarse a cualquier escala: desde operaciones corporativas complejas hasta la gestión ágil de un negocio o proyecto específico.
        </p>
        <p>
          La organización se centra en <strong>Sectores</strong>, espacios flexibles que agrupan tus solicitudes de compra (como una obra, una sucursal o un departamento), manteniendo todo ordenado y bajo control.
        </p>
        <p>
          Nuestra <strong>Inteligencia Artificial</strong> actúa como tu asistente experto en cada paso:
        </p>
        <ul className="mt-4 list-disc pl-4">
          <li><strong>Digitaliza todo:</strong> Desde solicitudes y presupuestos hasta remitos y facturas. Esto genera automáticamente un historial de precios para que puedas comparar y decidir mejor en el futuro.</li>
          <li><strong>Analiza:</strong> La IA evalúa la calidad de cada solicitud y cotización específica, ayudándote a saber si un precio es bueno independientemente de la comparativa.</li>
          <li><strong>Controla:</strong> Valida la recepción de materiales (remitos), asegurando la calidad y cantidad de lo que entra a tu empresa.</li>
        </ul>
      </section>

      <section>
        <h2>0. Flujo de trabajo en 1 minuto</h2>
        <p>Una visión general de cómo Synapse transforma un pedido en una orden de compra:</p>
        <ol>
          <li><strong>Digitalización:</strong> Subí un PDF o pegá texto; la IA extrae los ítems automáticamente.</li>
          <li><strong>Gestión:</strong> Revisá y enriquecé la solicitud con datos clave.</li>
          <li><strong>Cotización:</strong> Invitá proveedores o cargá sus presupuestos en PDF.</li>
          <li><strong>Comparativa:</strong> Visualizá el mejor precio por ítem y el ahorro potencial.</li>
          <li><strong>Decisión:</strong> Elegí proveedores, aprobá según montos y generá las órdenes de compra.</li>
        </ol>
      </section>

      <section>
        <h2>1. Digitalización de Solicitudes con IA</h2>
        <p>
          Olvidate de la carga manual. Desde &quot;Solicitudes de Compras&quot;, hacé clic en &quot;Nueva Solicitud&quot; y dejá que la IA haga el trabajo pesado.
        </p>
        <ul>
          <li><strong>Archivos soportados:</strong> PDF, JPG/JPEG, PNG.</li>
          <li><strong>Procesamiento inteligente:</strong> La IA lee el documento y crea la solicitud con todos sus ítems detallados.</li>
          <li><strong>Flexibilidad:</strong> Si el archivo es complejo o manuscrito, siempre podés ajustar los resultados manualmente.</li>
        </ul>
        <VideoEmbed
          src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663024/creacion-por-archivo_b5ylnl.mp4"
          title="Video: Creación automática desde archivo"
        />
      </section>

      <section>
        <h3>1.b Solicitud Rápida (Texto a Solicitud)</h3>
        <p>
          Ideal para pedidos informales (WhatsApp, correos). Convertí texto libre en una solicitud estructurada en segundos.
        </p>
        <ol>
          <li>Entrá a &quot;Solicitudes de Compras&quot; &gt; &quot;Solicitud rápida&quot;.</li>
          <li>Pegá tu pedido en el campo de texto (ej: &quot;Necesito 10 cascos y 5 amoladoras para la obra centro&quot;).</li>
          <li>Hacé clic en &quot;Procesar&quot;. La IA detectará productos, cantidades y unidades.</li>
        </ol>
        <VideoEmbed src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663025/creacion-por-texto_eqgzq5.mp4" title="Video: De texto a solicitud estructurada" />
        
        <h4>Edición y Control</h4>
        <ul>
          <li><strong>Productos sugeridos:</strong> El sistema vincula lo detectado con tu catálogo existente.</li>
          <li><strong>Reprocesamiento:</strong> Si agregás más texto, podés volver a procesar para actualizar la lista.</li>
          <li><strong>Creación final:</strong> Una vez conforme, convertí el borrador en una Solicitud oficial.</li>
        </ul>
        <VideoEmbed src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663026/creacion-sc-simple_atlk6y.mp4" title="Video: Creación manual (sin IA)" />
      </section>

      <section>
        <h2>2. Gestión y Enriquecimiento de la Solicitud</h2>
        <p>
          Centralizá toda la información necesaria para comprar bien. Todo es editable y queda registrado.
        </p>
        <ul>
          <li><strong>Datos clave:</strong> Definí sector, fechas de entrega, prioridades y autorizadores.</li>
          <li><strong>Histórico de precios:</strong> Al cargar ítems, el sistema te muestra precios pagados anteriormente para referencia.</li>
          <li><strong>Colaboración:</strong> Agregá comentarios y adjuntos para que Compras tenga todo el contexto.</li>
        </ul>
        <VideoEmbed src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663024/agregar-item-manual-sc_lstoc6.mp4" title="Video: Gestión de ítems" />
        <VideoEmbed src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663030/historico-precios-sc-index_zhdagj.mp4" title="Video: Consulta de precios históricos" />
        <VideoEmbed src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663024/comentarios-adjunto_zdvwpl.mp4" title="Video: Colaboración en la solicitud" />
      </section>

      <section>
        <h2>3. Gestión de Cotizaciones y Proveedores</h2>
        <p>Synapse ofrece dos caminos para obtener precios: autogestión o carga interna.</p>
        
        <h3>3.A Carga de Cotizaciones (IA)</h3>
        <p>
          Si recibís presupuestos por mail, subí los PDFs a la plataforma. Nuestra IA extrae los precios, condiciones y monedas automáticamente, estandarizando la información para compararla.
        </p>
        <VideoEmbed
          src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663030/carga-manual-cotizacion-procesado_zhe33t.mp4"
          title="Video: Procesamiento de cotizaciones PDF"
        />
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
          <ImageFigure
            src="https://res.cloudinary.com/dlsujsulr/image/upload/v1763668062/SCR-20251120-orxs_pmeca1.png"
            alt="Ir a cotización"
            caption="Acceso rápido a cotizaciones"
          />
          <ImageFigure
            src="https://res.cloudinary.com/dlsujsulr/image/upload/v1763668064/SCR-20251120-osaq_e9j6vz.png"
            alt="Asignar proveedor"
            caption="Asignación inteligente de proveedor"
          />
        </div>

        <h3>3.B Portal de Proveedores</h3>
        <p>
          Enviá invitaciones directas desde la plataforma. Los proveedores acceden a un link seguro para cargar sus precios, eliminando la carga de datos interna.
        </p>
        <VideoEmbed
          src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663027/enviar-solicitud-cotizacion-mail_beyujo.mp4"
          title="Video: Envío de invitaciones"
        />
        <VideoEmbed src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663029/portal-cotizaciones-desde-email_cgjkce.mp4" title="Video: Vista del proveedor" />
      </section>

      <section>
        <h2>4. Revisión y Normalización</h2>
        <p>
          Asegurá que estás comparando &quot;peras con peras&quot;. El sistema ayuda a validar que lo cotizado coincida con lo solicitado.
        </p>
        <ul>
          <li><strong>Tarjetas de resumen:</strong> Vista rápida de cada oferta con sus totales y monedas.</li>
          <li><strong>Detalle:</strong> Ajustá condiciones comerciales, plazos y monedas si es necesario.</li>
        </ul>
        <VideoEmbed src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663029/detalle-cotizacion_n28723.mp4" title="Video: Revisión de ofertas" />
      </section>

      <section>
        <h3>4.1 Match Inteligente de Productos</h3>
        <p>
          El corazón de la comparativa. Synapse vincula los códigos del proveedor con tus artículos internos.
        </p>
        <ul>
          <li><strong>Automático:</strong> El sistema aprende de tus asociaciones previas.</li>
          <li><strong>Manual:</strong> Herramientas visuales para corregir o asignar nuevos productos rápidamente.</li>
        </ul>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
          <ImageFigure
            src="https://res.cloudinary.com/dlsujsulr/image/upload/v1763668061/SCR-20251120-orpv_iubyqs.png"
            alt="Match cotización"
            caption="Match detallado"
          />
          <ImageFigure
            src="https://res.cloudinary.com/dlsujsulr/image/upload/v1763668060/SCR-20251120-ormo_ym3qzq.png"
            alt="Match overlay"
            caption="Asignación rápida"
          />
        </div>
      </section>

      <section>
        <h2>5. Comparativa de Precios y Ahorro</h2>
        <p>
          Tomá decisiones basadas en datos. La tabla comparativa cruza todas las ofertas normalizadas.
        </p>
        <ul>
          <li><strong>Semáforo de precios:</strong> Identificá visualmente la mejor oferta (verde) y la segunda mejor.</li>
          <li><strong>Multimoneda:</strong> Conversión automática a una moneda base para comparar ofertas en ARS y USD.</li>
          <li><strong>Exportable:</strong> Generá un PDF profesional de la comparativa para firmas o archivo.</li>
        </ul>
        <VideoEmbed
          src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663029/comparativa-con-cambio-plazo-de-pago_zwztfs.mp4"
          title="Video: Análisis de comparativa"
        />
        <ImageFigure
          src="https://res.cloudinary.com/dlsujsulr/image/upload/v1763668427/SCR-20251120-ovwb_osfb9n.png"
          alt="Comparativa de precios"
          caption="Tabla comparativa unificada"
        />
      </section>

      <section>
        <h3>5.b Flujos de Aprobación Configurables</h3>
        <p>
          Garantizá el cumplimiento de políticas internas. Configurá niveles de autorización basados en montos por sector.
        </p>
        <VideoEmbed
          src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663027/crear-aprobacion-en-sector_nzd93r.mp4"
          title="Video: Configuración de reglas"
        />
        <ul>
          <li><strong>Jerarquías:</strong> Definí quién aprueba según el valor de la compra.</li>
          <li><strong>Notificaciones:</strong> Alertas automáticas a los responsables cuando se requiere su firma.</li>
        </ul>
        <div className="rounded-xl bg-amber-50 p-4 text-amber-800">
          <h4 className="font-semibold">Importación con múltiples aprobadores por nivel</h4>
          <p className="mt-2 text-sm">
            Al importar niveles de aprobación desde Excel, podés asignar varios usuarios a un mismo nivel separando los emails con punto y coma (<code>;</code>).
          </p>
          <p className="mt-2 text-sm">
            <strong>Ejemplo:</strong> <code>maria@empresa.com; juan@empresa.com; pedro@empresa.com</code>
          </p>
        </div>
        <VideoEmbed src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663027/pedir-aprobacion_hdn0ji.mp4" title="Video: Solicitud de aprobación" />
        <VideoEmbed src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663028/rechazar-aprobacion-sc_k5j99h.mp4" title="Video: Gestión de aprobaciones" />
      </section>

      <section>
        <h2>6. Selección y Generación de Órdenes</h2>
        <p>
          Cerrá el ciclo de compra. Elegí al proveedor ganador (total o parcial) y generá los documentos formales.
        </p>
        <ul>
          <li><strong>Selección flexible:</strong> Podés adjudicar ítems a distintos proveedores buscando el máximo ahorro.</li>
          <li><strong>Órdenes de Compra:</strong> Generación automática de PDFs listos para enviar al proveedor.</li>
        </ul>
        <VideoEmbed src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663028/seleccion-precios-a-comprar_apxefv.mp4" title="Video: Adjudicación y cierre" />
      </section>

      <section>
        <h2>7. Trazabilidad y Auditoría</h2>
        <p>
          Mantené el control total. Cada acción en la plataforma queda registrada.
        </p>
        <ul>
          <li><strong>Historial de cambios:</strong> Quién modificó qué y cuándo.</li>
          <li><strong>Estados claros:</strong> Seguimiento en tiempo real del avance de cada solicitud.</li>
          <li><strong>Comentarios:</strong> Hilo de conversación unificado por operación.</li>
        </ul>
      </section>

      <section>
        <h2>8. Glosario de Estados</h2>
        <p>Entendé en qué etapa se encuentra cada operación:</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-slate-200 p-4">
            <h4 className="font-semibold text-slate-900">Compras</h4>
            <ul className="mt-2 text-sm text-slate-600">
              <li><strong>En revisión:</strong> Nueva solicitud recibida.</li>
              <li><strong>Cotizando:</strong> Se están buscando precios.</li>
              <li><strong>Pendiente de aprobación:</strong> Esperando firma de gerencia.</li>
            </ul>
          </div>
          <div className="rounded-lg border border-slate-200 p-4">
            <h4 className="font-semibold text-slate-900">Operaciones</h4>
            <ul className="mt-2 text-sm text-slate-600">
              <li><strong>Orden emitida:</strong> Compra confirmada al proveedor.</li>
              <li><strong>Entrega parcial:</strong> Se recibieron algunos ítems.</li>
              <li><strong>Completada:</strong> Pedido cerrado exitosamente.</li>
            </ul>
          </div>
        </div>
      </section>

      <section>
        <h2>9. Preguntas Frecuentes</h2>
        <ul className="space-y-2">
          <li><strong>¿Por qué no veo la comparativa?</strong> Verificá que hayas asignado un proveedor a las cotizaciones cargadas.</li>
          <li><strong>¿Cómo manejo dólares?</strong> Si una cotización es en USD, asegurate de cargar el tipo de cambio en la comparativa.</li>
          <li><strong>¿Puedo subir imágenes?</strong> Sí, la IA procesa fotos de remitos o facturas igual que PDFs.</li>
        </ul>
      </section>

      <section>
        <h2>10. Importación Masiva</h2>
        <p>
          Mantené tu base de datos al día. Importá catálogos de productos y padrones de proveedores desde Excel.
        </p>
        <p>
          <strong>Tip:</strong> Usá las mismas categorías (Rubro/Subrubro) en ambos archivos para mantener la consistencia de los reportes.
        </p>
      </section>

      <section>
        <h2>11. Compañías: Entidades Legales para Facturación</h2>
        <p>
          Las Compañías representan las entidades legales de tu organización. Cada Compañía tiene datos fiscales propios que se utilizan para generar Órdenes de Compra con la información correcta de facturación.
        </p>
        
        <h3>¿Por qué configurar Compañías?</h3>
        <ul>
          <li><strong>Facturación correcta:</strong> Las Órdenes de Compra incluyen automáticamente logo, razón social, CUIT, dirección fiscal y condición de IVA de la Compañía.</li>
          <li><strong>Multi-entidad:</strong> Gestioná varias empresas (filiales, distintas personas jurídicas) en un mismo sistema sin mezclar datos.</li>
          <li><strong>Control por sector:</strong> Cada Obra/Sector puede pertenecer a una Compañía distinta; las OC salen con los datos correspondientes.</li>
        </ul>

        <h3>Crear y configurar una Compañía</h3>
        <ol>
          <li>Ingresá a &quot;Compañías&quot; desde el menú lateral.</li>
          <li>Completá los datos:
            <ul>
              <li><strong>Nombre:</strong> Nombre comercial (obligatorio).</li>
              <li><strong>Razón social:</strong> Nombre legal completo (opcional).</li>
              <li><strong>CUIT:</strong> Número sin guiones ni espacios (obligatorio).</li>
              <li><strong>Dirección fiscal:</strong> Dirección completa que aparecerá en la OC.</li>
              <li><strong>Condición fiscal:</strong> Responsable Inscripto, Autónomo, Monotributista, etc.</li>
              <li><strong>Logo:</strong> Imagen que aparece en las Órdenes de Compra.</li>
            </ul>
          </li>
          <li>Asigná la Compañía al Sector desde la ficha de la Obra.</li>
        </ol>

        <div className="rounded-xl bg-blue-50 p-4 text-blue-800">
          <h4 className="font-semibold">En las Órdenes de Compra</h4>
          <p className="mt-2 text-sm">
            Cuando generás una OC, el PDF incluye automáticamente todos los datos fiscales de la Compañía asignada al Sector: logo, razón social, CUIT, dirección fiscal y condición de IVA. Así el proveedor sabe exactamente a quién facturar.
          </p>
        </div>

        <VideoEmbed
          src="https://res.cloudinary.com/dlsujsulr/video/upload/v1765903116/company-demo-guide_cc0btu.mp4"
          title="Video: Configuración de Compañías"
        />
      </section>

      <section>
        <h2>12. Recepción: Gestión de Remitos</h2>
        <p>
          Controlá lo que entra. Digitalizá remitos para validar entregas contra órdenes de compra.
        </p>
        <ol>
          <li>Ingresá a &quot;Remitos&quot; y subí la foto o PDF del documento.</li>
          <li>La IA extrae los ítems recibidos.</li>
          <li>Vinculá el remito a la Solicitud original para descontar cantidades pendientes.</li>
        </ol>
        <VideoEmbed src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663025/cargar-remito-desde-sc-evidencias-recibido_lgoo66.mp4" title="Video: Carga y control de remitos" />
      </section>

      <section>
        <h2>13. Facturación y Compras Directas</h2>
        <p>
          Cerrá el circuito administrativo. Procesá facturas automáticamente y vinculalas a las solicitudes.
        </p>
        <ul>
          <li><strong>Extracción automática:</strong> Fecha, número, proveedor, ítems y totales (neto, IVA, total).</li>
          <li><strong>Compra Directa:</strong> Si llega una factura sin solicitud previa, podés crear la operación directamente desde la factura para regularizarla.</li>
        </ul>
        <VideoEmbed src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663025/agregar-factura-desde-sc_tgt2oy.mp4" title="Video: Gestión de facturas" />
      </section>

      <section>
        <h2>14. La importancia del &quot;Match&quot;</h2>
        <p>
          El &quot;Match&quot; es el traductor universal de Synapse. Vincula cómo llama tu proveedor a un producto (ej: &quot;Casco seg. amarillo&quot;) con tu código interno (ej: &quot;EPP-001&quot;).
        </p>
        <div className="rounded-xl bg-blue-50 p-4 text-blue-800">
          <h4 className="font-semibold">¿Por qué es vital?</h4>
          <ul className="mt-2 list-disc pl-4 text-sm">
            <li>Permite comparar precios históricos reales.</li>
            <li>Habilita el control de stock y entregas.</li>
            <li>Mejora las predicciones de la IA con el tiempo.</li>
          </ul>
        </div>
      </section>

      <section>
        <h2>15. Ciclos de Re-aprobación</h2>
        <p>
          La flexibilidad es clave. Si una compra es rechazada (ej: por precio alto), podés ajustar la selección de proveedores y volver a enviarla a aprobar sin perder el historial.
        </p>
        <VideoEmbed
          src="https://res.cloudinary.com/dlsujsulr/video/upload/v1763663027/pedir-nuevo-aprobacion-tras-rechazo_blih2c.mp4"
          title="Video: Flujo de re-aprobación"
        />
      </section>

      <section className="rounded-2xl p-8 text-center text-white">
        <h2 className="text-white">¿Necesitás soporte adicional?</h2>
        <p className="mt-4 text-slate-300">
          Nuestro equipo está para ayudarte a sacar el máximo provecho de Synapse.
        </p>
        <div className="mt-6">
          <Button asChild variant="secondary" size="lg">
            <a href="https://tally.so/r/nre9Ll" target="_blank" rel="noreferrer">
              Contactar a Soporte
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
}
